import { debounce } from "@solid-primitives/scheduled";
import { ConceptsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent, subscribeToServerEvent } from "@storytell/events";
import type { Concept } from "@storytell/types";
import { createResource, createSignal, onCleanup, onMount, startTransition } from "solid-js";
import { isServer } from "solid-js/web";
import { createResourceStorage } from "../../lib/createResourceStorage";
import { createEntitySyncManager } from "../sync/createEntitySyncManager";

export type ConceptsService = ReturnType<typeof createConceptsService>;

export const createConceptsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
  projectId: () => string | undefined;
  conceptsSynced: () => void;
}) => {
  const client = useStorytellClient();

  const concepts = createEntitySyncManager<Concept>((concept) => [concept.name, concept.description, concept.summary]);

  const storage = createResourceStorage(
    () => concepts.all(),
    (value) => {
      concepts.hydrateFromSync(value || []);
      deps.conceptsSynced();
    }
  );

  // The resource carries initialValue [], so a store that has never synced reads exactly like an
  // empty one. Anything that distinguishes "no concepts" from "not fetched yet" needs this.
  const [synced, setSynced] = createSignal(false);

  // projectId is the reactive key: returning it gates the request on the
  // X-Project-Id header and triggers a refetch on project navigation.
  // The resource's value is consumed through `storage` below, not this binding.
  const [, { refetch }] = createResource(
    () => {
      if (isServer || !deps.authReady() || !deps.userId()) return false;
      return deps.projectId() ?? false;
    },
    async (): Promise<Concept[]> => {
      const request = ConceptsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      setSynced(true);
      if (response.isErr()) {
        console.error("Failed to fetch concepts sync", response.error);
        return [];
      }
      setSynced(true);

      const data = Object.values(response.value.concepts);
      if (isServer) {
        storage[1](data);
      }
      return data;
    },
    {
      initialValue: [] as Concept[],
      deferStream: true,
      storage: () => storage,
    }
  );

  // Debounced refetch function wrapped in startTransition
  const debouncedRefetch = debounce(() => {
    void startTransition(() => {
      void refetch();
    });
  }, 750);

  onMount(() => {
    const unsubscribeUserContext = subscribeToGlobalEvent("user-context-changed", () => {
      void refetch();
    });
    const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
      void refetch();
    });
    // Named concepts are merged into the synced graph, and cp.named_concepts is
    // the only concept kind the server publishes — the graph itself lives in
    // ClickHouse and emits nothing.
    const unsubscribeServerEvent = subscribeToServerEvent("cp.named_concepts", () => {
      debouncedRefetch();
    });

    onCleanup(() => {
      unsubscribeUserContext();
      unsubscribeConnectivityRestored();
      unsubscribeServerEvent();
      debouncedRefetch.clear();
    });
  });

  return {
    synced,
    refetch,
    ...concepts,
  };
};
